<template>
  <div :class="$style.wrapper">
    <component
      :is="DynamicTable" 
      :title="'Performance'"
      :headers="headers"
      :rows="originalRows"
      :loading-data="loadingData"
    />
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, resolveDynamicComponent } from 'vue';
  import { useStore } from 'vuex';
  import BaseTable from '@components/common/BaseTable.vue';
  import MobileTable from '@components/common/MobileTable.vue';
  import reportsService from '@/services/reportsService';

  const store = useStore();
  const originalRows = ref([]);
  const loadingData = ref(true);
  
  const isMobile = computed(() => store.getters['device/isMobile']);
  
  const DynamicTable = computed(() =>
    resolveDynamicComponent(isMobile.value ? MobileTable : BaseTable)
  );

  const headers = [
    { title: 'Year',  key: 'year' },
    { title: 'Jan', key: 'jan' },
    { title: 'Feb', key: 'feb' },
    { title: 'Mar', key: 'mar' },
    { title: 'Apr', key: 'apr' },
    { title: 'May', key: 'may' },
    { title: 'Jun', key: 'jun' },
    { title: 'Jul', key: 'jul' },
    { title: 'Aug', key: 'aug' },
    { title: 'Sep', key: 'sep' },
    { title: 'Oct', key: 'oct' },
    { title: 'Nov', key: 'nov' },
    { title: 'Dec', key: 'dec' },
    { title: 'Yearly Total', key: 'total' },
    { title: 'Accumulated', key: 'accumulated' },
  ];

  const formatData = async (data) => {
    const monthsMap = {
      '01': 'jan', '02': 'feb', '03': 'mar', '04': 'apr',
      '05': 'may', '06': 'jun', '07': 'jul', '08': 'aug',
      '09': 'sep', '10': 'oct', '11': 'nov', '12': 'dec',
    };

    const formattedRows = {};

    Object.entries(data.monthly_gains).forEach(([key, value]) => {
      const [year, month] = key.split('-');
      const monthKey = monthsMap[month];

      if (!formattedRows[year]) {
        formattedRows[year] = {
          year,
          jan: '--', feb: '--', mar: '--', apr: '--',
          may: '--', jun: '--', jul: '--', aug: '--',
          sep: '--', oct: '--', nov: '--', dec: '--',
          total: '--',
          accumulated: '--'
        };
      }

      formattedRows[year][monthKey] = `${Math.trunc(value * 100) / 100}%`;
    });

    Object.keys(formattedRows).forEach((year) => {
      if (data.yearly_gains[year]) {
        formattedRows[year].total = `${Math.trunc(data.yearly_gains[year] * 100) / 100}%`;
      }
      if (data.total_gains_by_year[year]) {
        formattedRows[year].accumulated = `${Math.trunc(data.total_gains_by_year[year] * 100) / 100}%`;
      }
    });

    return Object.values(formattedRows).sort((a, b) => b.year - a.year);
  };

  const fetchData = async () => {
    try {
      loadingData.value = true
      const tableData = await reportsService.getPerformanceTableData();
      originalRows.value = await formatData(tableData);
    } catch (error) {
      originalRows.value = [];
    } finally {
      loadingData.value = false;
    }
  };

  onMounted(async () => {
    await fetchData();
  });
</script>

<style module lang="scss">
  .wrapper {
    gap: 25px;
    display: flex;
    flex-wrap: wrap;
    margin-top: 20px;
    flex-direction: column;
  }
</style>